'use client';

import type { CSSProperties } from 'react';
import Image from 'next/image';
import { HUB_EVENTS, pushDataLayer } from '@/lib/analytics';
import { useInView } from '@/lib/hooks/useInView';
import { useParallax } from '@/lib/hooks/useParallax';
import { ArrowUpRight } from './icons';
import type { HubContent } from '@/lib/content/hub';

export default function Giveaway({ copy }: { copy: HubContent['giveaway'] }) {
  const parallaxRef = useParallax<HTMLDivElement>(0.08, 10);
  const { ref: cardRef, inView: cardInView } = useInView<HTMLAnchorElement>();
  const { ref: copyRef, inView: copyInView } = useInView<HTMLDivElement>();

  return (
    <section id="win" className="hub-section bg-cream py-20 text-coal sm:py-28">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          {/* Clickable prize card → FanBuilder */}
          <a
            ref={cardRef}
            href={copy.url}
            target="_blank"
            rel="noreferrer"
            aria-label={copy.sweepAria}
            onClick={() => pushDataLayer(HUB_EVENTS.GIVEAWAY_CLICK, { location: 'prize_card' })}
            className={`group relative block overflow-hidden bg-coal transition-all duration-700 ease-out focus-visible:ring-4 focus-visible:ring-yamal focus-visible:outline-none active:opacity-95 ${
              cardInView ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'
            }`}
          >
            <div className="relative aspect-[4/5] overflow-hidden">
              <div
                ref={parallaxRef}
                style={
                  {
                    '--parallax-y': '0px',
                    transform: 'translate3d(0, var(--parallax-y), 0) scale(1.02)',
                  } as CSSProperties
                }
                className="absolute inset-0 will-change-transform"
              >
                <Image
                  src={copy.image}
                  alt=""
                  fill
                  sizes="(min-width: 1024px) 50vw, 100vw"
                  className="object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
                />
              </div>
            </div>
            <div className="bg-coal p-6 sm:p-8">
              <p className="hub-label text-yamal">{copy.sweepEyebrow}</p>
              <p className="hub-display mt-3 text-[clamp(1.6rem,3.4vw,2.4rem)] text-cream">
                {copy.cardPrize[0]}
                <br />
                {copy.cardPrize[1]}
              </p>
              <span className="hub-display mt-5 inline-flex min-h-[52px] items-center gap-2 bg-yamal px-6 py-4 text-[20px] tracking-[0.02em] text-coal transition-transform duration-300 group-hover:translate-x-1">
                {copy.cta}
                <ArrowUpRight className="size-5" />
              </span>
            </div>
          </a>

          {/* Prize copy */}
          <div
            ref={copyRef}
            style={{ transitionDelay: '100ms' }}
            className={`transition-all duration-700 ease-out ${
              copyInView ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'
            }`}
          >
            <p className="hub-label-lg text-lamine">{copy.eyebrow}</p>
            <h2 className="hub-display mt-4 text-[clamp(2.2rem,6vw,4rem)] text-navy">
              {copy.headline}
            </h2>
            <p className="mt-6 max-w-lg text-[17px] leading-relaxed text-coal/80">
              {copy.body.map((line, index) => (
                <span key={line}>
                  {index > 0 && (
                    <>
                      <br />
                      <br />
                    </>
                  )}
                  {line}
                </span>
              ))}
            </p>
            <dl className="mt-8 grid gap-px border border-coal/15 bg-coal/15 sm:grid-cols-3">
              {copy.rows.map((row) => (
                <div key={row.label} className="bg-cream p-4">
                  <dt className="hub-label-sm text-coal/60">{row.label}</dt>
                  <dd className="mt-2 text-[18px] leading-snug font-semibold text-navy">
                    {row.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>
    </section>
  );
}
